import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import {
  lifecycle,
  platformEmergence,
  platformLayers,
} from "@/features/building/catalog";
import { headingText } from "@/lib/heading";

function BuildingLinks({
  links,
}: {
  links: ReadonlyArray<{ to: string; label: string } | { href: string; label: string }>;
}) {
  return (
    <p>
      {links.map((link, index) => (
        <span key={link.label}>
          {index > 0 ? " · " : null}
          {"to" in link ? (
            <Link to={link.to}>{link.label}</Link>
          ) : (
            <a href={link.href}>{link.label}</a>
          )}
        </span>
      ))}
    </p>
  );
}

export function BuildingPage() {
  return (
    <>
      <Seo
        title="Building"
        description="Platform engineering as the capability that delivers predictive products: how a service-delivery platform emerges, the stack that sustains it, and the lifecycle from prediction story to reliability in production."
        path="/building"
      />
      <section className="wrap section" style={{ paddingTop: "4.5rem" }}>
        <p className="eyebrow">Building</p>
        <h1>Platform engineering for predictive products</h1>
        <p className="lede">
          A platform arrives late. Once it is stable, platform engineering is
          the capability that sustains a service-delivery platform so a
          predictive offering can be operated.
        </p>
        <div className="prose">
          <p>
            Industry platforms expand growth. Service-delivery platforms sell
            access rather than objects. Capabilities keep an offering alive while
            processes stay as routines. That argument was written down in{" "}
            <Link to="/resources/built-to-thrive">Built to Thrive</Link>; the
            work now is to operate it. The six stages below are those routines.
            ecosystem.Ai is the predictive offering. EcosystemCode is part of the
            stable core.
          </p>
          <p>
            Social platforms that replace a stage-gate sit with{" "}
            <Link to="/work-with-jay#innovation-systems">innovation systems</Link>
            . This page is the stack that carries a predictive product.
          </p>
        </div>
      </section>

      <section className="wrap section">
        <p className="section-kicker">Emergence</p>
        <p className="lede" style={{ marginBottom: "var(--space-4)" }}>
          Platforms do not appear overnight. Breakthrough, lag, then a second
          generation stable enough for new service models.
        </p>
        <div className="arch-flow">
          {platformEmergence.map((step) => (
            <article className="card" key={step.id} id={step.id}>
              <h3>{headingText(step.title)}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="wrap section">
        <p className="section-kicker">Capability</p>
        <p className="lede" style={{ marginBottom: "var(--space-4)" }}>
          Once the platform is stable, this is the architecture you operate —
          from the business model the person meets, down to the core that has to
          stay common.
        </p>
        <ol className="layer-stack">
          {platformLayers.map((layer) => (
            <li key={layer.id} id={layer.id}>
              <p className="meta">{layer.step}</p>
              <h2>{headingText(layer.title)}</h2>
              <p>{layer.book}</p>
              <p>{layer.product}</p>
              <BuildingLinks links={layer.links} />
            </li>
          ))}
        </ol>
      </section>

      <section className="wrap section">
        <p className="section-kicker">Routines</p>
        <p className="lede" style={{ marginBottom: "var(--space-4)" }}>
          These stages are processes inside the capability. They do not replace
          the platform.
        </p>
        <ol className="timeline">
          {lifecycle.map((stage) => (
            <li key={stage.id} id={stage.id}>
              <p className="meta">{stage.step}</p>
              <h2>{headingText(stage.title)}</h2>
              <p>{stage.body}</p>
              <BuildingLinks links={stage.links} />
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}
